import { FC, useMemo } from "react";
import Sparkline from "../components/Sparkline/Sparkline";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingAggregate from "../hooks/useTrackingAggregate";
import { autoBucketFor, type Period } from "../utils/period";
import { EventNames } from "../utils/eventNames";
import { type TrackingEvent } from "../hooks/useTrackingEvents";

interface Props {
	period: Period;
	availableEvents: Set<string>;
	allEvents: TrackingEvent[];
}

const isoWeekIndex = (d: Date): number => {
	const tmp = new Date(
		Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	);
	const dayNum = (tmp.getUTCDay() + 6) % 7;
	tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
	const firstThursday = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
	return (
		Math.round((tmp.getTime() - firstThursday.getTime()) / (7 * 86400000)) + 1
	);
};

const LongTermTrendsTab: FC<Props> = ({
	period,
	availableEvents,
	allEvents
}) => {
	const sessionAvail = availableEvents.has(EventNames.SESSION_START);
	const bucket = autoBucketFor(period);
	const sessions = useTrackingAggregate(
		{
			event: EventNames.SESSION_START,
			from: period.from,
			to: period.to,
			groupBy: bucket
		},
		{ enabled: sessionAvail }
	);

	const totalEverRecorded = useMemo(
		() => allEvents.reduce((acc, e) => acc + (e.total_count ?? 0), 0),
		[allEvents]
	);

	const heatmap = useMemo(() => {
		const map = new Map<number, number[]>();
		(sessions.data?.buckets ?? []).forEach((b) => {
			const d = new Date(`${b.bucket}T00:00:00Z`);
			if (isNaN(d.getTime())) return;
			const year = d.getUTCFullYear();
			const wi = isoWeekIndex(d) - 1;
			const arr = map.get(year) ?? new Array<number>(53).fill(0);
			if (wi >= 0 && wi < 53) {
				arr[wi] = (arr[wi] ?? 0) + b.count;
			}
			map.set(year, arr);
		});
		return Array.from(map.keys())
			.sort()
			.map((y) => ({ year: y, weekCounts: map.get(y)! }));
	}, [sessions.data]);

	const maxHeat = useMemo(
		() =>
			heatmap.reduce(
				(m, r) =>
					Math.max(
						m,
						r.weekCounts.reduce((a, b) => Math.max(a, b), 0)
					),
				1
			),
		[heatmap]
	);

	return (
		<div className="space-y-8">
			<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<p className="text-sm text-muted-foreground mb-1">
						Cumulative events ever recorded
					</p>
					<p className="text-2xl font-heading font-semibold text-foreground">
						{totalEverRecorded.toLocaleString()}
					</p>
					<p className="text-xs text-muted-foreground mt-1">
						Sum across all event names returned by /tracking/events/.
					</p>
				</div>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<p className="text-sm text-muted-foreground mb-1">
						All-time DAU sparkline ({bucket})
					</p>
					{sessionAvail && sessions.data ? (
						<Sparkline
							ariaLabel={`All-time active users by ${bucket}`}
							data={sessions.data.buckets.map(
								(b) => b.distinct_user_count ?? b.count
							)}
							valueLabel="users"
						/>
					) : (
						<p className="text-sm text-muted-foreground">No data yet</p>
					)}
				</div>
			</div>

			<div className="rounded-xl border border-border bg-white shadow-sm p-4 overflow-x-auto">
				<h3 className="font-heading text-base font-medium mb-3">
					Seasonal heatmap (week × year)
				</h3>
				{!sessionAvail ? (
					<EmptyState
						title="No data yet"
						description="SESSION_START is not being emitted."
					/>
				) : heatmap.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						No data in this range.
					</p>
				) : (
					<figure
						aria-labelledby="heatmap-caption"
						aria-describedby="heatmap-data"
					>
						<figcaption id="heatmap-caption" className="sr-only">
							Seasonal session heatmap, week of year by year. Cell intensity
							encodes session count; the equivalent table below carries the
							underlying numbers.
						</figcaption>
						<div role="img" aria-label="Seasonal session heatmap">
							<svg
								viewBox={`0 0 ${53 * 12 + 40} ${heatmap.length * 16 + 34}`}
								className="w-full"
								style={{ minWidth: 600 }}
							>
								{/* Month markers along top */}
								{[
									{ label: "Jan", week: 0 },
									{ label: "Feb", week: 5 },
									{ label: "Mar", week: 9 },
									{ label: "Apr", week: 13 },
									{ label: "May", week: 18 },
									{ label: "Jun", week: 22 },
									{ label: "Jul", week: 26 },
									{ label: "Aug", week: 31 },
									{ label: "Sep", week: 35 },
									{ label: "Oct", week: 39 },
									{ label: "Nov", week: 44 },
									{ label: "Dec", week: 48 }
								].map((m) => (
									<text
										key={m.label}
										x={40 + m.week * 12}
										y={10}
										fontSize={9}
										fill="var(--color-muted-foreground)"
									>
										{m.label}
									</text>
								))}
								{heatmap.map((row, ri) => (
									<g key={row.year} transform={`translate(0, ${ri * 16 + 14})`}>
										<text
											x={0}
											y={12}
											fontSize={10}
											fill="var(--color-muted-foreground)"
										>
											{row.year}
										</text>
										{row.weekCounts.map((c, wi) => {
											const intensity = c === 0 ? 0 : c / maxHeat;
											const fill = `rgba(37, 99, 235, ${0.1 + intensity * 0.9})`;
											return (
												<rect
													key={wi}
													x={40 + wi * 12}
													y={2}
													width={10}
													height={12}
													rx={2}
													fill={c === 0 ? "var(--color-muted)" : fill}
												>
													<title>{`Week ${wi + 1}: ${c.toLocaleString()}`}</title>
												</rect>
											);
										})}
									</g>
								))}
							</svg>
						</div>
						{/* Colour legend */}
						<div
							className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground"
							aria-hidden="true"
						>
							<span>Fewer</span>
							<div className="flex gap-px">
								{[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
									<div
										key={intensity}
										className="w-3 h-3 rounded-sm"
										style={{
											background:
												intensity === 0
													? "var(--color-muted)"
													: `rgba(37, 99, 235, ${0.1 + intensity * 0.9})`
										}}
									/>
								))}
							</div>
							<span>More sessions</span>
						</div>
						{/* Visually hidden table is the keyboard- and screen-reader-
						    accessible representation of the heatmap. WCAG 1.1.1 /
						    1.4.1 — colour intensity alone is not sufficient. */}
						<table id="heatmap-data" className="sr-only">
							<caption>Sessions per ISO week per year</caption>
							<thead>
								<tr>
									<th scope="col">Year</th>
									{heatmap[0]?.weekCounts.map((_, wi) => (
										<th key={wi} scope="col">{`W${wi + 1}`}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{heatmap.map((row) => (
									<tr key={row.year}>
										<th scope="row">{row.year}</th>
										{row.weekCounts.map((c, wi) => (
											<td key={wi}>{c.toLocaleString()}</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</figure>
				)}
			</div>
		</div>
	);
};

export default LongTermTrendsTab;
